import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  DATE_FORMAT_OPTIONS,
  type DateFormat,
  updateOrganizationInputSchema,
  type UpdateOrganizationInput,
} from "@repo/shared";
import {
  Alert,
  Button,
  FeedbackState,
  Field,
  Form,
  FormActions,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
} from "@h2j-data-team/ui";
import { ConfirmDialog, useConfirmDialog, toast } from "@/components/shared";
import {
  organizationMembershipsQueryOptions,
  useUpdateOrganization,
  useUpdateOrganizationDateFormat,
  useDeleteOrganization,
} from "@/features/organizations/api";
import { useLocale } from "@/hooks/use-locale";
import {
  getStoredActiveOrganizationId,
  setActiveOrganizationId,
  getErrorMessage,
} from "@/lib/api-client";

// A fixed reference instant used only to preview each date format in the picker.
const DATE_FORMAT_SAMPLE = new Date(2026, 11, 31, 14, 30, 5);

export const Route = createFileRoute("/_authenticated/settings/organization")({
  staticData: { pageTitle: "breadcrumbs.orgSettings" },
  component: OrganizationSection,
});

function OrganizationSection() {
  const { t } = useTranslation("organizations");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const { confirm, dialogProps } = useConfirmDialog();
  const { dateFnsLocale } = useLocale();
  const deleteButton = useRef<HTMLButtonElement>(null);

  const { data: organizationMemberships = [], isLoading } = useQuery(
    organizationMembershipsQueryOptions,
  );

  const activeOrganizationId = getStoredActiveOrganizationId();
  const activeOrganization =
    organizationMemberships.find((org) => org.id === activeOrganizationId) ?? null;

  const isOwner = activeOrganization?.role === "owner";

  const renameForm = useForm<UpdateOrganizationInput>({
    resolver: zodResolver(updateOrganizationInputSchema),
    values: {
      name: activeOrganization?.name ?? "",
    },
  });

  const updateOrganization = useUpdateOrganization();
  const updateDateFormat = useUpdateOrganizationDateFormat();
  const deleteOrganization = useDeleteOrganization();

  const onDateFormatChange = async (value: string) => {
    if (!activeOrganizationId) return;
    try {
      await updateDateFormat.mutateAsync(value as DateFormat);
      toast.success(t("settings.dateFormat.success"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("settings.dateFormat.failed")));
    }
  };

  const onRenameSubmit = async (data: UpdateOrganizationInput) => {
    if (!activeOrganizationId) return;
    try {
      await updateOrganization.mutateAsync({ organizationId: activeOrganizationId, data });
      toast.success(t("settings.name.success"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("settings.name.failed")));
    }
  };

  const handleDelete = () => {
    if (!activeOrganizationId || !activeOrganization) return;

    confirm({
      title: t("settings.dangerZone.deleteTitle"),
      description: t("settings.dangerZone.deleteDescription", { name: activeOrganization.name }),
      confirmLabel: t("settings.dangerZone.deleteConfirmLabel"),
      variant: "destructive",
      restoreFocusRef: deleteButton,
      onConfirm: async () => {
        try {
          await deleteOrganization.mutateAsync(activeOrganizationId);
          // Switch to a remaining organization
          const remaining = organizationMemberships.find((org) => org.id !== activeOrganizationId);
          setActiveOrganizationId(remaining?.id ?? null);
          toast.success(t("settings.dangerZone.deleteSuccess"));
          navigate({ to: "/dashboard" });
        } catch (err) {
          toast.error(getErrorMessage(err, t("settings.dangerZone.deleteFailed")));
        }
      },
    });
  };

  if (isLoading) {
    return <FeedbackState state="loading" title={tc("loading")} />;
  }

  if (!activeOrganization) {
    return <FeedbackState state="empty" title={t("settings.noOrgSelected")} />;
  }

  return (
    <>
      <SettingsSection title={t("settings.layout.nav.organization.label")}>
        <Form
          busy={renameForm.formState.isSubmitting}
          onSubmit={renameForm.handleSubmit(onRenameSubmit)}
        >
          <Field
            label={t("settings.name.label")}
            hint={!isOwner ? t("settings.name.ownerOnly") : t("settings.name.helper")}
            error={renameForm.formState.errors.name?.message}
            className="max-w-[420px]"
          >
            <Input
              placeholder={t("settings.name.placeholder")}
              disabled={!isOwner}
              {...renameForm.register("name")}
            />
          </Field>
          {isOwner && (
            <FormActions
              submitLabel={t("settings.name.save")}
              submittingLabel={t("settings.name.saving")}
              className="justify-start"
            >
              <Button
                type="button"
                variant="ghost"
                onClick={() => renameForm.reset({ name: activeOrganization.name })}
              >
                {t("settings.name.cancel")}
              </Button>
            </FormActions>
          )}
        </Form>

        <Field
          label={t("settings.dateFormat.title")}
          hint={!isOwner ? t("settings.dateFormat.ownerOnly") : t("settings.dateFormat.helper")}
          className="max-w-[280px]"
        >
          {(control) => (
            <Select
              value={activeOrganization.dateFormat}
              onValueChange={onDateFormatChange}
              disabled={!isOwner}
            >
              <SelectTrigger {...control} loading={updateDateFormat.isPending}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMAT_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {format(DATE_FORMAT_SAMPLE, opt, { locale: dateFnsLocale })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      </SettingsSection>

      {isOwner && (
        <Alert
          tone="danger"
          role="note"
          title={t("settings.dangerZone.deleteOrg")}
          action={
            <Button ref={deleteButton} variant="danger" onClick={handleDelete}>
              {t("settings.dangerZone.deleteButton")}
            </Button>
          }
        >
          {t("settings.dangerZone.deleteDesc")}
        </Alert>
      )}

      <ConfirmDialog {...dialogProps} />
    </>
  );
}
